import { FormEvent, useState, DragEvent, ChangeEvent } from 'react';

const Contact = ({
  closeModal,
  customer,
}: {
  closeModal: () => void;
  customer?: string;
}) => {
  const [formState, setFormState] = useState<'initial' | 'loading' | 'sent'>(
    'initial',
  );
  const [displayImages, setDisplayImages] = useState<
    Array<string | ArrayBuffer>
  >([]);
  const [formImages, setFormImages] = useState<Array<File>>([]);
  const [mailOrNumber, setMailOrNumber] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [marketingApproval, setMarketingApproval] = useState<boolean>(false);

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (formState === 'sent') {
      closeModal();
    } else {
      setFormState('loading');
      submit();
    }
  };

  const submit = async () => {
    if (!mailOrNumber || !description) {
      setError('Fyll i e-postadress eller telefonnummer och beskrivning');
      setFormState('initial');
      return;
    } else {
      const formData = new FormData();
      formData.append('emailphone', mailOrNumber);
      formData.append('description', description);
      formData.append('marketingApproval', String(marketingApproval));
      formImages.forEach((file) => {
        formData.append('images[]', file);
      });
      try {
        const base = process.env.REACT_APP_WP_URL
          ? `${process.env.REACT_APP_WP_URL}/wp-json/headless`
          : 'https://hemfixarna.se/wp-json/headless';
        const res = await fetch(
          `${base}/sendpaintingcontact?customer=${customer}`,
          {
            method: 'POST',
            body: formData,
          },
        );
        const response = await res.json();
        if (response.status === 200) {
          setFormState('sent');
        } else {
          setError('Något gick fel, försök igen senare');
          setFormState('initial');
        }
      } catch (error) {
        setError('Något gick fel, försök igen senare');
        setFormState('initial');
      }
    }
  };

  const handleImageDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (!files) return;

    const loadPromises: Promise<void>[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();

        const loadPromise = new Promise<void>((resolve) => {
          reader.onload = () => {
            if (reader.result === null) return;
            setDisplayImages((prevDisplayImages) => [
              ...prevDisplayImages,
              reader.result as string,
            ]);
            setFormImages((prevFormImages) => [...prevFormImages, file]);
            resolve();
          };
        });

        loadPromises.push(loadPromise);
        reader.readAsDataURL(file);
      }
    }

    // Wait for all FileReader.onload promises to resolve before updating state
    Promise.all(loadPromises).catch((error) => {
      // Handle any errors that occurred during file loading
      console.error(error);
      setError('Något gick fel, försök igen senare');
    });
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const files = (e.target as HTMLInputElement).files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result === null) return;
          setDisplayImages([...displayImages, reader.result]);
          setFormImages([...formImages, file]);
        };

        reader.readAsDataURL(file);
      }
    }
  };

  const handleImageClick = (target: HTMLElement) => {
    (target.querySelector('.file-upload') as HTMLInputElement).click();
  };

  const preventDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const removeImage = (index: number) => {
    setDisplayImages(displayImages.filter((_, i) => i !== index));
    setFormImages(formImages.filter((_, i) => i !== index));
  };

  return (
    <div onClick={handleClickOutside} className="calculator_contact">
      <div className="calculator_modal calculator_modal--contact">
        <button
          onClick={closeModal}
          className="calculator_modal--close"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20px"
            height="20px"
            viewBox="-0.5 0 25 25"
            fill="none"
          >
            <path
              d="M3 21.32L21 3.32001"
              stroke="#000000"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M3 3.32001L21 21.32"
              stroke="#000000"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div>
          <h2>Kontakta mig istället</h2>
          <p>
            Fast pris på målning och tapetsering i hela Sverige, professionella
            och erfarna målare. Möjlighet till 30% ROT-avdrag på arbetet.
          </p>
          <form onSubmit={(e) => handleFormSubmit(e)}>
            <div>
              <input
                type="text"
                id="emailphone"
                placeholder="Skriv in din e-postadress eller telefonnummer"
                value={mailOrNumber}
                onChange={(e) => {
                  setError('');
                  setMailOrNumber(e.target.value);
                }}
              />
            </div>
            <div>
              <textarea
                id="description"
                value={description}
                placeholder="Beskriv vad du behöver ha hjälp med"
                onChange={(e) => {
                  setError('');
                  setDescription(e.target.value);
                }}
              />
            </div>
            <div>
              <div
                role="button"
                aria-label="upload image"
                onDragOver={(e) => preventDragOver(e)}
                onDrop={(e) => handleImageDrop(e)}
                onClick={(e) => handleImageClick(e.currentTarget)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11 16V7.85L8.4 10.45L7 9L12 4L17 9L15.6 10.45L13 7.85V16H11ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z"
                    fill="black"
                  />
                </svg>
                <div>
                  <p>Bifoga ev bilder</p>
                  <span>Klicka eller dra bilder hit</span>
                </div>
                <input
                  onChange={(e) => handleFileInputChange(e)}
                  className="file-upload"
                  accept="image/*"
                  type="file"
                  multiple
                  name="fileInput"
                />
              </div>
            </div>
            <div className="form-preview">
              {displayImages.map((img, i) => (
                <div key={i}>
                  <img src={img as string} alt={`Upload number ${i + 1}`} />
                  <button onClick={() => removeImage(i)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M19 6L6 19"
                        stroke="#C84E19"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L19 19"
                        stroke="#C84E19"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={marketingApproval}
                  onChange={(e) => setMarketingApproval(e.target.checked)}
                />
                Jag godkänner reklam och villkor
              </label>
            </div>
            {error ? <p className="calculator_qerror">{error}</p> : null}
            {formState === 'sent' ? (
              <p>Tack för din förfrågan! Vi återkommer så snart som möjligt.</p>
            ) : null}
            <button type="submit" className="primary_button">
              {formState === 'sent'
                ? 'Stäng'
                : formState === 'loading'
                  ? 'Skickar...'
                  : 'Kontakta mig'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
